import type { Dictionary, Language } from '@/i18n/types';

/**
 * One key, in one language, with its variables filled in.
 *
 * Separate from the hook so the rules below can be tested against the real dictionaries: the unit
 * suite runs in node with no DOM, so anything only reachable through a React hook is only reachable
 * through an e2e test, which is a slow way to ask what "1 proiect" should read as.
 *
 * Romanian is the base bundle: a key missing from `en` answers in Romanian rather than printing its
 * own name at the reader, the same rule the backend's message bundle follows.
 */
export function translate<D extends Dictionary>(
  dict: D,
  language: Language,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const table = dict[language];
  const fallback = dict.ro;

  // A counted noun does not keep one shape. Romanian has three — 1 proiect, 2 proiecte, 20 de
  // proiecte — and the catalogue printed the middle form for every number, so an institution with
  // a single project was labelled "1 proiecte" on its own card, and English read "1 results".
  //
  // Intl knows the rule for each language, so the categories are not hardcoded here; a key opts in
  // by defining `_one`/`_few`/`_other` beside itself, and a key that defines none is looked up
  // exactly as before. That matters for the keys where `count` is not a countable noun at all —
  // "{{count}}/300 de caractere" is a ratio, and has no plural to select.
  const plural =
    typeof vars?.count === 'number'
      ? `${key}_${new Intl.PluralRules(language).select(vars.count)}`
      : null;

  // This language first, in both its forms, before Romanian in either. Taking the plural variant
  // across languages would answer an English page with "3 de proiecte", because English defines no
  // `_few` and Romanian does — a fallback is meant to fill a gap, not to switch language mid-line.
  let text = table[plural ?? key] ?? table[key] ?? fallback[plural ?? key] ?? fallback[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.split(`{{${k}}}`).join(String(v));
    }
  }
  return text;
}
